import React, { useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePermissions } from '../context/PermissionContext';
import { authService } from '../services/authService';

// Auth Phase 2 (P2-A, rulings R2/R6): the D1 rolling-reset step. Shown by
// ProtectedRoute INSTEAD of the app when the session carries a forced-reset
// token — no dashboard chrome, no navigation, nothing else reachable. It calls
// the existing POST /api/auth/change-password (the one route such a token may
// reach besides /me), then completePasswordReset() refreshes the session so
// the server mints clean claims and the app proceeds.
//
// The client mirrors the server policy (12–72) only to save a round trip; the
// server is the authority (length + breach check). Its message is shown as-is.
const POLICY_MIN = 12;
const POLICY_MAX = 72;

const page = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'linear-gradient(135deg, #0a0a0a 0%, #1E1E1E 100%)', padding: 20
};
const card = {
  width: '100%', maxWidth: 440, background: '#1E1E1E', color: '#E0E0E0', border: '1px solid #2A2A2A',
  borderRadius: 20, padding: '40px 36px', boxShadow: '0 25px 50px rgba(0,0,0,0.5), 0 0 25px rgba(212,169,78,0.25)'
};
const label = { display: 'block', fontSize: 14, fontWeight: 500, color: '#E0E0E0', marginBottom: 6 };
const input = {
  width: '100%', boxSizing: 'border-box', padding: '12px 14px', fontSize: 16, borderRadius: 10,
  border: '2px solid #2A2A2A', background: '#141414', color: 'white', outline: 'none', marginBottom: 16
};
const hint = { fontSize: 13, color: '#A0A0A0', margin: '0 0 18px' };
const errorStyle = { fontSize: 13, color: '#EF4444', margin: '0 0 14px' };
const primary = {
  width: '100%', padding: 14, fontSize: 16, fontWeight: 600, borderRadius: 10, border: 0,
  background: '#D4A94E', color: '#1a1a1a', cursor: 'pointer'
};
const secondary = {
  width: '100%', padding: 10, marginTop: 10, fontSize: 14, borderRadius: 10,
  border: '1px solid #2A2A2A', background: 'transparent', color: '#A0A0A0', cursor: 'pointer'
};

const ForcedPasswordReset = () => {
  const { user, logout, completePasswordReset } = useAuth();
  const { refreshPermissions } = usePermissions();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const tooShort = newPassword.length > 0 && newPassword.length < POLICY_MIN;
  const tooLong = newPassword.length > POLICY_MAX;
  const mismatch = confirm.length > 0 && confirm !== newPassword;
  const canSubmit = !busy && currentPassword && newPassword.length >= POLICY_MIN && !tooLong && confirm === newPassword;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setError('');
    setBusy(true);
    try {
      await authService.changePassword(currentPassword, newPassword);
    } catch (err) {
      // The fetch client throws Error(<validator msg | server message | HTTP n>).
      setError(err.message || 'Could not change your password. Please try again.');
      setBusy(false);
      return;
    }
    try {
      // The password IS saved from here on. Refresh mints clean claims, then the
      // permissions that 403'd while the token was flagged are reloaded.
      await completePasswordReset();
      if (refreshPermissions) await refreshPermissions();
    } catch (err) {
      setError('Your password was saved, but the session could not be refreshed. Please sign out and sign in again.');
      setBusy(false);
    }
  };

  return (
    <div style={page} data-testid="forced-password-reset">
      <div style={card}>
        <img src="/gvc-logo.png" alt="GVC School Bus" style={{ width: 90, display: 'block', margin: '0 auto 14px' }} />
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#FFFFFF', margin: '0 0 6px', textAlign: 'center' }}>Set a new password</h1>
        <p style={{ ...hint, textAlign: 'center' }}>
          {user?.name ? `${user.name}, ` : ''}the portal's password rules have been updated. Choose a new password to continue.
        </p>
        <form onSubmit={submit} autoComplete="off">
          <label style={label}>Current password
            <input style={input} type="password" value={currentPassword} onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password" autoFocus disabled={busy} />
          </label>
          <label style={label}>New password
            <input style={input} type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password" disabled={busy} />
          </label>
          <label style={label}>Confirm new password
            <input style={input} type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
              autoComplete="new-password" disabled={busy} />
          </label>
          <p style={hint}>
            Your new password must be between {POLICY_MIN} and {POLICY_MAX} characters, and it is checked against known data breaches.
          </p>
          {tooShort && <p style={errorStyle}>At least {POLICY_MIN} characters.</p>}
          {tooLong && <p style={errorStyle}>At most {POLICY_MAX} characters.</p>}
          {mismatch && <p style={errorStyle}>Passwords don't match.</p>}
          {error && <p style={errorStyle} role="alert">{error}</p>}
          <button type="submit" style={primary} disabled={!canSubmit}>{busy ? 'Saving…' : 'Set new password and continue'}</button>
          <button type="button" style={secondary} onClick={logout} disabled={busy}>Sign out</button>
        </form>
      </div>
    </div>
  );
};

export default ForcedPasswordReset;
